<script setup lang="ts">

import { IonContent, IonSpinner, IonInfiniteScroll, IonInfiniteScrollContent, IonButton, IonItem, IonLabel, IonList, IonSearchbar, IonListHeader, IonIcon, IonChip } from "@ionic/vue"
import {ellipseOutline, checkmarkCircle, informationCircleOutline} from 'ionicons/icons';
import {JobsiteCategorySegment, fetchJobsites} from "@/helper/jobsite"
import {computed, PropType, Ref, ref, watch} from "vue"
import Modal from '@/components/Modal.vue'
import {AInesInterestedInJobsiteTypes, Jobsite, JobsiteType} from "@/graphql/generated/graphql";
import { JobsiteCategory } from "@/graphql/kapitelTypes";
import {getIconForJobsiteCategory, getLabelForJobsiteCategory} from "@/helper/jobsiteCategory"
import {IonFooter} from "@ionic/vue";
import ButtonSegment from "@/components/ButtonSegment.vue";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import {KapitelIcon} from "@/helper/icon";

const props = defineProps({
    categorize : {
        type: Boolean,
        required: false,
        default: true
    },
    alreadySelectedJobsites: {
        type: Array as PropType<Jobsite[]>,
        required: false,
        default: () => []
    },
    quickSelectJobsiteCategorySegments: {
        type: Array as PropType<JobsiteCategorySegment[]>,
        required: false,
        default: () => []   
    },
    title: {
        type: String,
        required: false,
        default: "Station finden"
    }
})


const emit = defineEmits(['select'])

const modelValue = defineModel()
const isOpen = ref(modelValue.value)
const modal = ref()

watch(()=>modelValue.value, value =>isOpen.value=value)

watch(() => isOpen.value, value => {
    modelValue.value = value
    if (value) {
    }else{
        loading.value = false
        searchResultSegment.jobsites.value = []
        segments.value = [searchResultSegment]
        pagination.value = 0
        searchQuery.value = ""
    }
})

// const isOpen = ref(false)
// watch(() => isOpen.value, value => emit('update:modelValue', value))

const jobsiteDetailModalData : Ref<number | undefined> = ref(undefined)

const loading = ref(false);
interface JobsiteListSegment {
    category: JobsiteCategory,
    jobsites: Ref<Jobsite[]>
}
const segments: Ref<JobsiteListSegment[]>= ref([])
const searchResultSegment : JobsiteListSegment = {category: JobsiteCategory.Other, jobsites: ref([]) as Ref<Jobsite[]>}
const pagination = ref(0)
const pageSize = 25;
const searchQuery = ref()

const filterSelection: Record<string, Ref<boolean>> = {};
Object.entries(AInesInterestedInJobsiteTypes).forEach(([key]) => {
    filterSelection[key] = ref(false)
});
const selectedJobsiteTypes = ref<Array<JobsiteType>>([])

const endOfSearchListReached = ref(false)

watch(()=> endOfSearchListReached.value, value=>{
    console.log("end of list: " + value)
})

const filterSegments = computed(() => {
    const result = Object.keys(filterSelection).map(key => { return {key: key, label: key.toUpperCase()} })
    result.unshift({key: 'all', label: 'Alle'})
    return result
})

const searchByNewQuery = target => {
    searchQuery.value = target
    resetList()
}

const filterSelectionChanged = (value: string) => {
    selectedJobsiteTypes.value = value === 'all'
        ? []
        : [{ name: value }]

    resetList()
}

const resetList = () => {
    pagination.value = 0
    searchResultSegment.jobsites.value = []
    segments.value = [searchResultSegment]
    endOfSearchListReached.value = false

    resetScrollPosition()

    if (prependingQuickSelectResults()) {
        segments.value.unshift(...props.quickSelectJobsiteCategorySegments.map(({category, jobsites}) => ({category, jobsites: ref(jobsites)})))
    }

    fetchNextPageResults()    
}




const prependingQuickSelectResults = () => !searchQuery.value

const fetchNextPageResults = () => {
    loading.value = true;
    const currentSearchQuery = searchQuery.value
    const excludeJobsites = prependingQuickSelectResults() ? props.quickSelectJobsiteCategorySegments.reduce((agg: Array<Jobsite> ,segment) => agg.concat(segment.jobsites), []) : []

    let jobsiteTypeFilter: JobsiteType[]|undefined = undefined
    if(selectedJobsiteTypes.value.length>0){
        jobsiteTypeFilter = selectedJobsiteTypes.value
    }

    return fetchJobsites(searchQuery.value, pagination.value, pageSize, excludeJobsites, jobsiteTypeFilter)
        .then(gqlResult => {
            
            //discard race conditioned queryies
            if(currentSearchQuery != searchQuery.value){
                return 
            }

            if(gqlResult.length==0 || gqlResult[(gqlResult).length-1].isLastItem){
                endOfSearchListReached.value = true
            }

            searchResultSegment.jobsites.value.push(...gqlResult.map((sj) => sj.jobsite))

        })
        .finally(() => loading.value = false);
}

const ionInfinite = (ev) => {
    pagination.value = pagination.value + pageSize;
    fetchNextPageResults()
        .then(()=>{
            ev.target.complete()
        })
}

const onClick = (item: Jobsite) => {
    modal.value.dismiss().then(()=>{emit('select', item)})
}

function resetScrollPosition() {
    console.log("reset scroll position")
    document.querySelector('.jobsite-selector-modal-scroller')?.scrollToTop()
}

function isAlreadySelected(jobsite: Jobsite) {
    return !!props.alreadySelectedJobsites.find(item => item.id === jobsite.id)
}

defineExpose({
    searchByNewQuery
})

</script>

<template>
<Modal
    v-model="isOpen"
    :title="props.title"
    class="no-padding-left no-padding-right"
    ref="modal"
    fullscreen
    :toolbar="true"
    keep-footer-on-keyboard-show
>
    <template v-slot:default>
        <ion-content>
            <ion-list>
                <template v-for="segement in segments" v-bind:key="segement.category">
                    <IonListHeader 
                        v-if="segments.length > 1 && props.categorize && segement.jobsites.length > 0"
                        style="padding-left: var(--custom-spacing-app-content-padding-horizontal)"
                    >
                        <span>{{ getLabelForJobsiteCategory(segement.category) }}</span>
                    </IonListHeader>

                    <ion-item
                        v-for="item in segement.jobsites as Jobsite[]"
                        :detail="false"
                        :key="item.id"
                        @click="onClick(item)"
                        :class="isAlreadySelected(item) ? 'alreadySelected' : ''"
                        :button="true"
                    >
                        <ion-label class="app-padding-horizontal">
                            <h3>
                                {{ item.name }}
                                <IonIcon v-if="item.isFavorite" :icon="KapitelIcon.favoriteJobsite" class="globalTextAlignedIcon" color="primary"></IonIcon>
                                <IonIcon v-if="item.isAlternative" :icon="KapitelIcon.alternativeJobsite" class="globalTextAlignedIcon" color="primary"></IonIcon>
                            </h3>
                            <p>{{ item.client?.abbreviation }}<span v-if="item.house">, {{ item.house?.abbreviation }}</span><span v-if="item.additionalAmenImportantAbbreviations && item.additionalAmenImportantAbbreviations?.length>0">, {{item.additionalAmenImportantAbbreviations.join(", ")}}</span></p>
                        </ion-label>
                        <!--<ion-icon aria-hidden="true" :icon="checkmarkCircle" color="primary" slot="start" v-if="isAlreadySelected(item)" />-->
                        <!--<ion-icon aria-hidden="true" :icon="ellipseOutline" color="medium" slot="end" v-else />-->
                        <ion-button
                            slot="end"
                            class="jobsiteInfo app-padding-right"
                            fill="clear"
                            color="secondary"
                            @click.stop="jobsiteDetailModalData=item.id"
                        >
                            <ion-icon
                                slot="icon-only"
                                :icon="KapitelIcon.info"
                            />
                        </ion-button>
                    </ion-item>
                </template>
            </ion-list>
            <ion-infinite-scroll
                :disabled="endOfSearchListReached"
                @ionInfinite="ionInfinite"
            >
                <ion-infinite-scroll-content />
            </ion-infinite-scroll>
        </ion-content>
        <JobsiteDetails v-model="jobsiteDetailModalData" />
    </template>

    <template v-slot:actions>
        <div class="jobsite-selector-filter-buttons">
            <ButtonSegment
                :segments="filterSegments"
                @update:model-value="filterSelectionChanged"
            />
        </div>
        <ion-searchbar
            :debounce=200
            @ion-input="({target})=>searchByNewQuery(target.value)"
            :value="searchQuery ? searchQuery : ''"
            placeholder="Suchen ..."
            class="jobsite-selector-searchbar"
        />
    </template>
</Modal>
</template>

<style lang="scss" scoped>
.jobsite-selector-modal-scroller {
    height: 50vh;
    overflow: auto;
}

.jobsite-selector-searchbar {
    padding-left: 0;
    padding-right: 0;
}

.jobsite-selector-actions {
    padding-top: 1rem;
}

ion-footer {
    --custom-spacing-app-content-padding-horizontal: 0
}

.alreadySelected ion-label {
    opacity: 0.25;
}
.jobsite-selector-filter-buttons {
    margin-top: 1em;
}

.jobsite-search-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2em;
}


ion-item {
    --padding-end: 0;
    --custom-spacing-app-content-padding-horizontal: 0;
}
ion-button {
    --padding-end: 0;
    --custom-spacing-app-content-padding-horizontal: 0;
}

</style>
